<template>
  <video
    v-if="isShowVideo"
    ref="videoRef"
    :autoplay="autoplay"
    :controls="controls"
    :loop="loop"
    :muted="muted"
    :playsinline="playsinline"
    :poster="poster"
    :preload="preload"
    @canplay="videoCanPlay"
    @ended="emits('ended')"
    @pause="emits('pause')"
    @play="emits('play')"
  >
    <source
      :src="videoUrl"
      type="video/mp4"
    />
    Your browser doesn't support HTML5 video tag.
  </video>
  <CNuxtImg
    v-else-if="fallbackPicture"
    :src="fallbackPicture"
    alt="video poster"
    min-height="100%"
  />
</template>

<script lang="ts" setup>
import { useVideoLocaleUrl } from '~/composables/videoLocaleUrl.ts';

const props = defineProps({
  loop: {
    type: Boolean,
    default: true,
  },
  preload: {
    type: String,
    default: 'auto',
  },
  autoplay: {
    type: Boolean,
    default: true,
  },
  playsinline: {
    type: Boolean,
    default: true,
  },
  poster: {
    type: String,
  },
  fallbackPoster: {
    type: String,
  },
  muted: {
    type: Boolean,
    default: true,
  },
  src: {
    type: String,
  },
  localized: {
    type: Boolean,
    default: false,
  },
  playDelay: {
    type: Number,
    default: 0,
  },
  controls: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['ended', 'play-started', 'play', 'pause']);
const videoRef = ref<HTMLVideoElement | null>(null);
const route = useRoute();
const { getVideoLocaleUrl } = useVideoLocaleUrl();
const timeOutId = ref<ReturnType<typeof setTimeout>>();

onMounted(() => {
  if (timeOutId.value) {
    clearTimeout(timeOutId.value);
  }
  if (!isShowVideo.value) {
    timeOutId.value = setTimeout(() => {
      emits('play-started');
      emits('ended');
    }, props.playDelay);
  }
});

const isShowVideo = computed(() => route.query.hv !== '1');

const videoUrl = computed(() => {
  if (props.localized && props.src) {
    return getVideoLocaleUrl(props.src);
  }

  return props.src;
});

const fallbackPicture = computed(() => {
  if (!props.localized) {
    return props.fallbackPoster || props.poster;
  }
  if (props.fallbackPoster) {
    return getVideoLocaleUrl(props.fallbackPoster, 'images/fallback-posters');
  }

  return props.poster;
});

function onPlay() {
  videoRef.value?.play();
}

function resetVideoTime() {
  if (videoRef.value) {
    videoRef.value.currentTime = 1;
  }
}

function videoCanPlay(event: Event) {
  if (props.playDelay === -1) {
    return;
  }

  const el = (event.target as HTMLVideoElement) ?? videoRef.value;

  if (props.playDelay > 0) {
    setTimeout(() => {
      emits('play-started');
      el.play();
    }, props.playDelay);
  } else {
    emits('play-started');
    el.play();
  }
}

defineExpose({
  resetVideoTime,
  onPlay,
});
</script>

<style lang="scss" scoped>

</style>
